import { TToolKey, getToolByName } from "@highway/mbs-shared";
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { AnalyticsManager } from "../../core/AnalyticsManager";
import {
    IAnalyticsContextValue,
    IAnalyticsPlatformConfig,
    IEventDefinition,
    ISetupUserParams,
    TTrackCleanupFn,
} from "../../types";

const AnalyticsContext = createContext<IAnalyticsContextValue | null>(null);

interface IAnalyticsProviderProps {
    children: ReactNode;
    config: IAnalyticsPlatformConfig;
    appEvents?: Record<string, IEventDefinition>; // Optional app-specific events
}

export const AnalyticsProvider = ({ children, config, appEvents }: IAnalyticsProviderProps) => {
    const analyticsManager = useMemo(() => new AnalyticsManager(config, appEvents), [config, appEvents]);
    const [eventPrefix, setEventPrefixState] = useState<string>("");

    useEffect(() => {
        analyticsManager.initialize();
    }, []);

    const setEventPrefix = (prefix: string) => {
        analyticsManager.setEventPrefix(prefix); // Update the class property
        setEventPrefixState(prefix); // Update React state to trigger re-render
    };

    const value: IAnalyticsContextValue = {
        track: (
            eventKey: string,
            properties?: Record<string, any>,
            context?: Record<string, any>,
            cleanup?: () => void | Promise<void>,
        ) => analyticsManager.track(eventKey, properties, context, cleanup),
        reset: () => analyticsManager.reset(),
        setupUser: (params: ISetupUserParams) => analyticsManager.setupUser(params),
        setEventPrefix,
        eventPrefix,
    };

    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (tool?: TToolKey): IAnalyticsContextValue => {
    const context = useContext(AnalyticsContext);

    if (!context) {
        throw new Error("useAnalytics must be used within an AnalyticsProvider");
    }

    //NOTE - Value is only set, if there's a passed in tool argument
    const value = useMemo(() => {
        if (!tool) return context;

        const toolInfo = getToolByName(tool);
        const toolContext = {
            tool: toolInfo?.tool,
            toolKey: toolInfo?.toolKey,
            // Not setting toolPrefix not to overwrite specific tool variant prefixes
        };

        return {
            ...context,
            track: (
                eventKey: string,
                properties?: Record<string, any>,
                trackContext?: Record<string, any>,
                cleanup?: TTrackCleanupFn,
            ) => context.track(eventKey, properties, { ...toolContext, ...trackContext }, cleanup),
        };
    }, [context, tool]);

    return value;
};
